/**
 * PhotoCardActionButton — Iter. 406.h
 *
 * Reusable action button overlay for photo cards (transformation timeline,
 * meal gallery, recipe images, etc.). Designed mobile-first: the same anti-
 * pattern that hid Iter. 406.f delete button on iPhone (`opacity-0
 * group-hover:opacity-100`) is structurally impossible here.
 *
 * Visual contract:
 *   • 36×36 px touch target (Apple HIG min for thumbnail overlay).
 *   • Always visible (no hover gate).
 *   • Strong contrast: white icon on `bg-black/70` by default.
 *   • Optional `tone` prop to switch color: 'neutral' (default) | 'danger' |
 *     'success' | 'primary'.
 *   • `ring-1 ring-white/30 shadow-lg` so the button stays readable on both
 *     light and dark photos.
 *   • `z-20` to sit above the photo content.
 *   • `active:scale-95` for tactile feedback on touch.
 *
 * Behaviour contract:
 *   • Stops propagation on click so taps don't bubble to the photo's parent
 *     (which usually opens a fullscreen viewer).
 *   • Optional confirm step when `confirm` prop is truthy: a window.confirm
 *     prompt is shown before firing the action.
 *
 * Usage:
 *   <PhotoCardActionButton
 *     icon={Trash2}
 *     tone="danger"
 *     onClick={() => handleDelete(photo.id)}
 *     aria-label="Supprimer la photo"
 *     testId={`delete-photo-${photo.id}`}
 *   />
 */
import React from 'react';

const TONE_CLASSES = {
  neutral: 'bg-black/70 hover:bg-black/85',
  danger: 'bg-black/70 hover:bg-red-600',
  success: 'bg-black/70 hover:bg-emerald-600',
  primary: 'bg-black/70 hover:bg-primary',
};

const POSITION_CLASSES = {
  'top-right': 'top-1.5 right-1.5',
  'top-left': 'top-1.5 left-1.5',
  'bottom-right': 'bottom-1.5 right-1.5',
  'bottom-left': 'bottom-1.5 left-1.5',
};

export default function PhotoCardActionButton({
  icon: Icon,
  onClick,
  tone = 'neutral',
  position = 'top-right',
  confirm = null, // string message → window.confirm before firing
  ariaLabel,
  'aria-label': ariaLabelAlias,
  testId,
  className = '',
  disabled = false,
}) {
  const label = ariaLabel || ariaLabelAlias || 'action';

  const handleClick = (e) => {
    e.stopPropagation();
    e.preventDefault();
    if (disabled) return;
    if (confirm && !window.confirm(confirm)) return;
    onClick?.(e);
  };

  return (
    <button
      type="button"
      onClick={handleClick}
      disabled={disabled}
      aria-label={label}
      data-testid={testId}
      className={`absolute ${POSITION_CLASSES[position] || POSITION_CLASSES['top-right']} z-20 w-9 h-9 rounded-full ${TONE_CLASSES[tone] || TONE_CLASSES.neutral} text-white flex items-center justify-center shadow-lg ring-1 ring-white/30 active:scale-95 transition-colors disabled:opacity-40 disabled:cursor-not-allowed ${className}`}
    >
      {Icon ? <Icon className="w-4 h-4" /> : null}
    </button>
  );
}
